/* ==========================================================================
   card-product-premium.js  ·  <premium-card>

   A custom element so behaviour auto-(re)initialises whenever Dawn's
   facets.js replaces the grid HTML after filter / sort / pagination.
   (Inline <script> injected via innerHTML never runs again; a defined
   custom element upgrades automatically on insertion — that is the whole
   reason this is a web component and not the reference section's IIFE.)

   Per card it handles:
     - image fade nav (per-image arrows + dots)
     - hover image swap (desktop pointers only, opt-in)
     - variant swatches (price / compare / image / availability)
     - wishlist (localStorage, app-agnostic)
     - add to bag (reuses the theme's <cart-drawer>/<cart-notification>;
       falls back to a /cart/add POST if neither is present)

   It deliberately does NOT touch the grid, facets, pagination or sorting.
   ========================================================================== */

if (!customElements.get('premium-card')) {
  customElements.define(
    'premium-card',
    class PremiumCard extends HTMLElement {
      connectedCallback() {
        if (this._init) return; // guard against double-upgrade
        this._init = true;

        this.media = this.querySelector('[data-pcard-media]');
        this.imgs = this.media
          ? Array.prototype.slice.call(this.media.querySelectorAll('.pcard__media-img'))
          : [];
        this.dots = Array.prototype.slice.call(this.querySelectorAll('[data-pcard-dots] .pcard__dot'));
        this.current = 0;
        this.navigated = false;

        this.priceEl = this.querySelector('[data-pcard-price]');
        this.compareEl = this.querySelector('[data-pcard-compare]');
        this.atc = this.querySelector('[data-pcard-atc]');
        this.atcLabel = this.atc ? this.atc.querySelector('[data-pcard-atc-label]') : null;

        this.bindImageNav();
        this.bindHoverSwap();
        this.bindSwatches();
        this.bindWishlist();
        this.bindAddToCart();
      }

      /* ---------- image nav ---------- */
      show(i) {
        if (!this.imgs.length) return;
        i = (i + this.imgs.length) % this.imgs.length;
        this.current = i;
        this.imgs.forEach((im, idx) => im.classList.toggle('is-active', idx === i));
        this.dots.forEach((d, idx) => d.classList.toggle('is-active', idx === i));
      }

      bindImageNav() {
        this.querySelectorAll('[data-media-dir]').forEach((b) => {
          b.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.navigated = true;
            this.show(b.getAttribute('data-media-dir') === 'next' ? this.current + 1 : this.current - 1);
          });
        });
      }

      /* ---------- hover swap ---------- */
      bindHoverSwap() {
        const enabled = this.getAttribute('data-hover-swap') === 'true';
        if (!enabled || this.imgs.length < 2 || !this.media) return;
        if (!window.matchMedia('(hover: hover)').matches) return;

        this.media.addEventListener('mouseenter', () => {
          if (!this.navigated && this.current === 0) this.show(1);
        });
        this.media.addEventListener('mouseleave', () => {
          if (!this.navigated) this.show(0);
        });
      }

      /* ---------- swatches ---------- */
      bindSwatches() {
        const swatches = this.querySelectorAll('[data-pcard-swatch]');
        swatches.forEach((sw) => {
          sw.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();

            swatches.forEach((o) => {
              o.classList.remove('is-active');
              o.setAttribute('aria-pressed', 'false');
            });
            sw.classList.add('is-active');
            sw.setAttribute('aria-pressed', 'true');

            this.setAttribute('data-variant-id', sw.getAttribute('data-variant-id'));
            const avail = sw.getAttribute('data-available') === 'true';
            this.setAttribute('data-available', avail);

            // swap to the variant's image if it is present in this card
            const mid = sw.getAttribute('data-media-id');
            if (mid) {
              const idx = this.imgs.findIndex((im) => im.getAttribute('data-media-id') === mid);
              if (idx > -1) {
                this.navigated = true;
                this.show(idx);
              }
            }

            if (this.priceEl && sw.getAttribute('data-price')) {
              this.priceEl.textContent = sw.getAttribute('data-price');
            }
            if (this.compareEl) {
              const cp = sw.getAttribute('data-compare');
              if (cp) {
                this.compareEl.textContent = cp;
                this.compareEl.hidden = false;
              } else {
                this.compareEl.hidden = true;
              }
            }

            if (this.atc) {
              if (avail) {
                this.atc.removeAttribute('disabled');
                if (this.atcLabel) this.atcLabel.textContent = this.getAttribute('data-atc-label') || 'Add to bag';
              } else {
                this.atc.setAttribute('disabled', 'disabled');
                if (this.atcLabel) this.atcLabel.textContent = this.getAttribute('data-soldout-label') || 'Sold out';
              }
            }
          });
        });
      }

      /* ---------- wishlist ---------- */
      bindWishlist() {
        const wish = this.querySelector('[data-pcard-wishlist]');
        if (!wish) return;
        const KEY = 'pcard-wishlist';
        const pid = this.getAttribute('data-product-id');

        let list = [];
        try {
          list = JSON.parse(localStorage.getItem(KEY) || '[]');
        } catch (e) {
          list = [];
        }
        if (list.indexOf(pid) > -1) wish.setAttribute('aria-pressed', 'true');

        wish.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          try {
            list = JSON.parse(localStorage.getItem(KEY) || '[]');
          } catch (err) {
            list = [];
          }
          const on = wish.getAttribute('aria-pressed') === 'true';
          if (on) {
            list = list.filter((x) => x !== pid);
            wish.setAttribute('aria-pressed', 'false');
          } else {
            if (list.indexOf(pid) === -1) list.push(pid);
            wish.setAttribute('aria-pressed', 'true');
          }
          try {
            localStorage.setItem(KEY, JSON.stringify(list));
          } catch (err) {
            /* storage full / blocked — ignore */
          }
        });
      }

      /* ---------- add to cart ---------- */
      bindAddToCart() {
        const fire = (btn) => {
          if (this.getAttribute('data-available') !== 'true') return;
          this.addToCart(this.getAttribute('data-variant-id'), btn);
        };
        if (this.atc) {
          this.atc.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            fire(this.atc);
          });
        }
        const quick = this.querySelector('[data-pcard-quickadd]');
        if (quick) {
          quick.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            fire(null);
          });
        }
      }

      getCart() {
        return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      }

      addToCart(variantId, btn) {
        const addUrl = this.getAttribute('data-add-url') || '/cart/add.js';
        const cart = this.getCart();
        const labelEl = btn ? btn.querySelector('[data-pcard-atc-label]') : null;
        if (btn) {
          btn.classList.add('is-loading');
          btn.setAttribute('disabled', 'disabled');
        }

        const body = { items: [{ id: variantId, quantity: 1 }] };
        if (cart && typeof cart.getSectionsToRender === 'function') {
          body.sections = cart.getSectionsToRender().map((s) => s.id);
          body.sections_url = window.location.pathname;
          if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
        }

        fetch(addUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
          body: JSON.stringify(body),
        })
          .then((r) => r.json())
          .then((data) => {
            if (data.status) {
              if (labelEl) {
                const t = labelEl.textContent;
                labelEl.textContent = data.description || 'Unavailable';
                setTimeout(() => {
                  labelEl.textContent = t;
                }, 1800);
              }
              return;
            }
            if (cart && typeof cart.renderContents === 'function') {
              if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
              cart.renderContents(data);
            } else {
              const _psIcon = document.querySelector('.ps-cart');
              if (_psIcon) _psIcon.click();
            }
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'premium-card', productVariantId: variantId });
            }
          })
          .catch((e) => console.error('[premium-card] add to cart:', e))
          .finally(() => {
            if (btn) {
              btn.classList.remove('is-loading');
              btn.removeAttribute('disabled');
            }
          });
      }
    }
  );
}
